// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { mdiCalculator, mdiUndo } from "@mdi/js";
import { Input, Switch } from "antd";
import styled from "styled-components";

import { useScreenshotSettingContext } from "../hooks";

interface InputWihUnitModel {
  type: string;
  value?: string | number | boolean;
  checked?: boolean;
  step?: number;
  disabled?: boolean;
  title?: string;
  min?: number;
  max?: number;
  unit?: string;
  valueProperty?: string;
  id?: string;
  onChange?: (val: unknown) => void;
}

export default function ScreenshotSettingItemInput({
  type,
  value,
  checked,
  step,
  disabled,
  min,
  max,
  unit,
  id,
  onChange
}: InputWihUnitModel) {
  const [ownedValue, setOwnedValue] = useState<string | number | boolean>(
    value ?? checked
  );
  const [isCalculated, setIsCalculated] = useState<boolean>(false);
  // Tracked in a ref, not state: focus/blur must NOT re-run the sync effect, or
  // blur would snap ownedValue back to a still-stale prop and flicker the input.
  const isFocusedRef = useRef(false);

  const { name, calculatedValue } = useScreenshotSettingContext();

  // Sync ownedValue from the canonical external value whenever it changes,
  // but skip non-boolean inputs while the user is actively typing.
  useEffect(() => {
    if (isFocusedRef.current && type !== "boolean") return;

    if (type === "boolean") {
      const nextVal =
        checked !== undefined
          ? checked
          : calculatedValue !== undefined
          ? !!calculatedValue
          : false;
      setOwnedValue(nextVal);
    } else if (value !== undefined && value !== null) {
      setOwnedValue(value as string | number | boolean);
    } else if (calculatedValue !== undefined) {
      setOwnedValue(calculatedValue);
    }
  }, [value, checked, calculatedValue, type]);

  // Determine whether the current value matches the calculated default.
  useEffect(() => {
    const isCalc =
      type === "boolean"
        ? ownedValue === undefined || calculatedValue === ownedValue
        : ownedValue?.toString() === calculatedValue?.toString();

    setIsCalculated(isCalc);
  }, [calculatedValue, ownedValue, type]);

  const handleChange = useCallback(
    (e) => {
      const val = e.target.value;
      setOwnedValue(val);
      onChange && onChange(val);
    },
    [onChange]
  );

  const handleReset = useCallback(() => {
    setOwnedValue(calculatedValue);
    onChange && onChange(calculatedValue);
  }, [calculatedValue, onChange]);

  const handleBooleanChange = useCallback(
    (isChecked: boolean) => {
      setOwnedValue(isChecked);
      onChange && onChange(isChecked);
    },
    [onChange]
  );

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);
  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
  }, []);

  const hasCalculatedValue = !!calculatedValue;

  if (type === "boolean") {
    // Drive the switch from local ownedValue (set synchronously in handleBooleanChange)
    // so it flips on the same frame as the click instead of waiting for the
    // commit → re-render round-trip. The sync effect keeps ownedValue aligned with
    // the external `checked`/calculated value.
    const isChecked = ownedValue === undefined ? !!calculatedValue : !!ownedValue;

    return (
      <Switch
        id={id}
        size="small"
        checked={isChecked}
        onChange={handleBooleanChange}
        // Same handle the text/number branch below already emits — this branch
        // returns early and had been missing it, leaving every boolean setting
        // addressable only by `id`. antd puts it on the switch itself, which is
        // also what carries `ant-switch-checked`.
        data-qa={`screenshot-setting-${name}`}
        data-testid={`screenshot-setting-${name}`}
      />
    );
  }

  return (
    <Wrapper>
      <InputWrapper
        disabled={disabled}
        step={step}
        min={min}
        max={max}
        type={type}
        hasCalculated={hasCalculatedValue}
        isCalculated={isCalculated}
        title={(ownedValue ?? "").toString()}
        value={ownedValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        id={id}
        data-qa={`screenshot-setting-${name}`}
        data-testid={`screenshot-setting-${name}`}
      />
      {hasCalculatedValue && isCalculated && (
        <IconWrapper>
          <Icon path={mdiCalculator} size={1} />
        </IconWrapper>
      )}
      {hasCalculatedValue && !isCalculated && (
        <IconWrapper>
          <StyledIcon path={mdiUndo} size={1} onClick={handleReset} />
        </IconWrapper>
      )}
      {unit && <UnitLabel>{unit}</UnitLabel>}
    </Wrapper>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InputWrapper = styled(({ hasCalculated, isCalculated, ...props }) => (
  <Input {...props} />
))`
  background-color: ${(props) =>
    props.hasCalculated ? (props.isCalculated ? "#d5d5d5" : "#ebf8ef") : "#fff"};
  border-color: ${(props) =>
    props.hasCalculated ? (props.isCalculated ? "#d5f1ef" : "#a2efb8") : "#d5d5d5"};
`;

const Wrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: center;
  width: 100%;
  .ant-input {
    padding-right: 45px !important;
    width: 100%;
  }
`;
const IconWrapper = styled.div`
  font-size: 1rem;
  position: absolute;
  right: 25px;
  user-select: none;
  align-items: center;
  display: flex;
`;

const UnitLabel = styled.div`
  font-size: 1rem;
  position: absolute;
  right: 5px;
  user-select: none;
`;

const StyledIcon = styled(Icon)`
  &:hover {
    color: var(--color-primary);
  }
`;
